import { createContext, useContext, useSyncExternalStore } from "react";

/**
 * The time a page was rendered at, when a server rendered it: an ISO instant. Null in the
 * browser app, which has no server render to match.
 */
export const ClockContext = createContext<string | null>(null);

const subscribeToNothing = () => () => {};

/**
 * The time, the zone and the language a page's dates are told in.
 *
 * A server renders at its own moment and in UTC. The first render in the browser, the one that
 * hydrates that markup, must say the same words or React throws the markup away. So while it
 * hydrates, the clock is the server's: `renderedAt` in UTC. Right after, it is the real time and
 * the person's own zone and language (`timeZone` and `locale` undefined), and the dates
 * correct themselves. The server's language is `en-GB`, as the posting's day pills are set in.
 */
export function useClock(): {
    now: Date;
    timeZone: string | undefined;
    locale: string | undefined;
} {
    const renderedAt = useContext(ClockContext);
    // True on the server and while hydrating, false in every later render.
    const hydrating = useSyncExternalStore(
        subscribeToNothing,
        () => false,
        () => true,
    );
    // A render time that is no time is none: an invalid `Date` makes `Intl` throw.
    const rendered = renderedAt ? new Date(renderedAt) : null;
    if (hydrating && rendered && !Number.isNaN(rendered.getTime())) {
        return { now: rendered, timeZone: "UTC", locale: "en-GB" };
    }
    return { now: new Date(), timeZone: undefined, locale: undefined };
}
